import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { localization } from '@bb/contracts';
import { Button, ErrorState, Field, Input, ReadOnlyBanner, Select } from '@bb/ui';
import { ApiError, type EnvironmentLocale, type EnvironmentPatch, type EnvironmentSummary } from '../../api/client.ts';
import { environmentsQuery } from '../../api/queries.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { localeChain, normalizeLocale } from '../composer/shared/localization.ts';

/** A locale as the page edits it: `fallback` is the id of another row, so renaming a code keeps the link. */
interface Row {
  id: string;
  code: string;
  fallback: string;
}

interface Draft {
  rows: Row[];
  defaultId: string;
  attribute: string;
}

let lastRowId = 0;
const rowId = () => `locale-${++lastRowId}`;

/** The stored locales (or only `defaultLocale`); a fallback to a locale that is not listed cannot hold translations and is dropped. */
function toDraft(env: EnvironmentSummary): Draft {
  const list: EnvironmentLocale[] = env.settings?.locales?.length ? env.settings.locales : [{ code: env.defaultLocale ?? 'en', default: true }];
  const rows = list.map((l) => ({ id: rowId(), code: l.code, fallback: '' }));
  list.forEach((l, i) => {
    if (l.fallback) rows[i]!.fallback = rows.find((r) => r.code === l.fallback)?.id ?? '';
  });
  // Without a locale marked default, the environment's `defaultLocale` is the default (as the server reads it).
  const marked = list.findIndex((l) => l.default);
  const index = marked >= 0 ? marked : Math.max(0, list.findIndex((l) => normalizeLocale(l.code) === normalizeLocale(env.defaultLocale)));
  return { rows, defaultId: rows[index]!.id, attribute: env.settings?.localeAttribute ?? '' };
}

/** The PATCH body, or one message per row (`id`), for the list (`list`) or the attribute (`attribute`). */
function checkDraft(draft: Draft): { body: EnvironmentPatch | null; locales: EnvironmentLocale[]; issues: Record<string, string> } {
  const issues: Record<string, string> = {};
  const codes = new Map<string, string>();
  for (const row of draft.rows) {
    const code = normalizeLocale(row.code);
    if (!code) {
      issues[row.id] = 'Enter a language tag such as en or pt-BR.';
      continue;
    }
    if ([...codes.values()].includes(code)) issues[row.id] = `${code} is listed twice.`;
    codes.set(row.id, code);
  }
  const attribute = draft.attribute.trim() || 'language';
  if (attribute.length > 255) issues.attribute = 'Enter at most 255 characters.';
  const list = draft.rows.map((row) => {
    const fallback = row.id !== draft.defaultId && row.fallback ? codes.get(row.fallback) : undefined;
    return { code: codes.get(row.id) ?? row.code, ...(row.id === draft.defaultId ? { default: true } : {}), ...(fallback ? { fallback } : {}) };
  });
  const parsed = localization.EnvironmentLocales.safeParse(list);
  if (!parsed.success && Object.keys(issues).length === 0) issues.list = parsed.error.issues[0]?.message ?? 'The locales are not valid.';
  if (!parsed.success || Object.keys(issues).length) return { body: null, locales: [], issues };
  return { body: { defaultLocale: codes.get(draft.defaultId)!, settings: { locales: parsed.data, localeAttribute: attribute } }, locales: parsed.data, issues };
}

const errorText = (error: unknown) => {
  if (!(error instanceof ApiError)) return 'Something went wrong.';
  return (error.problem.issues as Array<{ path: string; message: string }> | undefined)?.map((i) => `${i.path}: ${i.message}`).join('; ') || error.message;
};

/**
 * Settings → Locales (BB-55): the languages messages are translated into, the default one (messages are written in
 * it), what each locale falls back to, and which profile attribute holds a user's language. Saving needs `env.manage`.
 */
export function EnvironmentLocalesPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/locales' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/locales' });
  const queryClient = useQueryClient();
  const canManage = usePermissions().has('env.manage');
  const environments = useQuery(environmentsQuery(api));
  const environment = environments.data?.find((e) => e.id === environmentId);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (environment && !dirty) setDraft(toDraft(environment));
    // Only a new environment value resets the form, and never over unsaved edits.
  }, [environment]);
  const save = useMutation({
    mutationFn: (body: EnvironmentPatch) => api.updateEnvironment(environmentId, body),
    onSuccess: async () => {
      setDirty(false);
      await queryClient.invalidateQueries({ queryKey: environmentsQuery(api).queryKey });
    },
  });

  if (environments.isError) return <ErrorState title="Locales could not be loaded" message={errorText(environments.error)} onRetry={() => void environments.refetch()} />;
  if (!draft) return <p aria-busy="true">Loading locales…</p>;

  const checked = checkDraft(draft);
  const edit = (next: Draft) => {
    setDraft(next);
    setDirty(true);
    save.reset();
  };
  const setRow = (id: string, patch: Partial<Row>) => edit({ ...draft, rows: draft.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  const remove = (id: string) => edit({ ...draft, rows: draft.rows.filter((r) => r.id !== id).map((r) => (r.fallback === id ? { ...r, fallback: '' } : r)) });
  const defaultCode = normalizeLocale(draft.rows.find((r) => r.id === draft.defaultId)?.code) ?? 'the default';
  const settings = checked.body ? { defaultLocale: checked.body.defaultLocale!, locales: checked.locales } : null;

  return (
    <section>
      <h1>Locales</h1>
      <p className="hint">One message carries every locale: a user gets the translation of their locale, else the locale it falls back to, else the default. Messages are written in the default locale.</p>
      {!canManage && <ReadOnlyBanner>Changing locales needs the env.manage permission.</ReadOnlyBanner>}
      <div className="stack-form">
        {draft.rows.map((row, index) => {
          const code = normalizeLocale(row.code);
          const isDefault = row.id === draft.defaultId;
          const name = code ?? `locale ${index + 1}`;
          return (
            <fieldset key={row.id} aria-label={`Locale ${index + 1}`}>
              <Field
                label="Code"
                error={checked.issues[row.id]}
                hint={settings && code ? `Users with ${code} get: ${localeChain(code, settings).join(' → ')}` : code && code !== row.code.trim() ? `Saved as ${code}` : undefined}
              >
                <Input value={row.code} spellCheck={false} placeholder="pt-BR" disabled={!canManage} onChange={(e) => setRow(row.id, { code: e.target.value })} />
              </Field>
              <label>
                <input
                  type="radio"
                  name="default-locale"
                  checked={isDefault}
                  disabled={!canManage}
                  aria-label={`${name} is the default locale`}
                  onChange={() => edit({ ...draft, defaultId: row.id, rows: draft.rows.map((r) => (r.id === row.id ? { ...r, fallback: '' } : r)) })}
                />{' '}
                Default
              </label>
              <Field label="Falls back to" hint={isDefault ? 'The default locale has no fallback.' : undefined}>
                <Select
                  value={isDefault ? '' : row.fallback}
                  disabled={!canManage || isDefault}
                  options={[{ value: '', label: `The default (${defaultCode})` }, ...draft.rows.filter((r) => r.id !== row.id && r.id !== draft.defaultId).map((r) => ({ value: r.id, label: normalizeLocale(r.code) ?? (r.code || '(no code yet)') }))]}
                  onChange={(e) => setRow(row.id, { fallback: e.target.value })}
                />
              </Field>
              <Button variant="ghost" size="sm" aria-label={`Remove ${name}`} disabled={!canManage || isDefault || draft.rows.length <= 1} onClick={() => remove(row.id)}>
                Remove
              </Button>
            </fieldset>
          );
        })}
        <div className="actions">
          <Button disabled={!canManage || draft.rows.length >= 50} onClick={() => edit({ ...draft, rows: [...draft.rows, { id: rowId(), code: '', fallback: '' }] })}>
            Add locale
          </Button>
        </div>
        {checked.issues.list && (
          <p className="bb-field__error" role="alert">
            {checked.issues.list}
          </p>
        )}
        <Field label="Language attribute" hint="Where a user’s language comes from: language (the standard attribute) or the key of a custom attribute, such as platform_language. Values like PT_br match pt-BR, and pt-BR matches pt." error={checked.issues.attribute}>
          <Input value={draft.attribute} placeholder="language" spellCheck={false} disabled={!canManage} onChange={(e) => edit({ ...draft, attribute: e.target.value })} />
        </Field>
        {canManage && (
          <div className="actions">
            <Button variant="primary" loading={save.isPending} disabled={!checked.body || !dirty} onClick={() => checked.body && save.mutate(checked.body)}>
              Save
            </Button>
          </div>
        )}
        {save.isSuccess && <p role="status">Locales saved.</p>}
        {save.isError && <p role="alert">{errorText(save.error)}</p>}
      </div>
    </section>
  );
}
